import React, { cloneElement, ReactElement } from 'react'
import { useMsg } from '~/common/localizationUtils'
import { Box, Button, ButtonProps, Flex, forwardRef, MenuList, useMenuButton } from '~/design_system'
import { ChevronDown } from '~/icons'
import { useMenuItemProps, MenuItems, DynamicSingleButtonOrMenuList } from '~/navigation_menu/constants'
import { HoverMenu, MenuItemLink, MenuItemLinkProps } from '~/navigation_menu/SubMenu'

export function MainMenusDesktop() {
  const menuItems = useMenuItemProps()
  const msg = useMsg()

  return (
    <Flex
      as="nav"
      aria-label={msg('nav.main_menu')}
      display="none"
      sx={{ '@media(min-width: 1350px)': { display: 'flex' }, '@media print': { display: 'none' } }}>
      <MenuButton {...menuItems.home.top} />
      <DesktopDynamicMenuList menuItems={menuItems.sow_menu} id="sow-menu" />
      <DesktopDynamicMenuList menuItems={menuItems.jobs_menu} id="jobs-menu" />
      <DesktopDynamicMenuList menuItems={menuItems.people_menu} id="people-menu" />
      <MenuButton {...menuItems.work_orders.top} />
      <DesktopDynamicMenuList menuItems={menuItems.invoices_menu} id="invoices-menu" />
      <DesktopDynamicMenuList menuItems={menuItems.timesheets_menu} id="timesheets-menu" />
      <MenuButton {...menuItems.dashboards.top} />
      <DesktopDynamicMenuList menuItems={menuItems.reports_menu} id="reports-menu" />
      {!menuItems.more_menu.top.isHidden && (
        <HoverMenu variant="mainMenu" id="more-menu">
          <HoverMenuButton {...menuItems.more_menu.top} />
          <MenuList>
            <DesktopMenuItemLink {...menuItems.more_menu.approvals} />
            <DesktopMenuItemLink {...menuItems.more_menu.documents} />
            <DesktopMenuItemLink {...menuItems.more_menu.vendors} />
            <DesktopMenuItemLink {...menuItems.more_menu.users} />
            <DesktopMenuItemLink {...menuItems.more_menu.program_teams} />
            <DesktopMenuItemLink {...menuItems.more_menu.company_profile} />
            <DesktopMenuItemLink {...menuItems.more_menu.worker_tracking} />
            <DesktopMenuItemLink {...menuItems.more_menu.expenses} />
            <DesktopMenuItemLink {...menuItems.more_menu.bulk_updates} />
            <DesktopMenuItemLink {...menuItems.more_menu.checklists} />
            <DesktopMenuItemLink {...menuItems.more_menu.position_management} />
            <DesktopMenuItemLink {...menuItems.more_menu.api_docs} />
            <DesktopMenuItemLink {...menuItems.more_menu.tenant_metadata} />
            <DesktopMenuItemLink {...menuItems.more_menu.company_settings} />
          </MenuList>
        </HoverMenu>
      )}
      <MenuButton {...menuItems.candidate_timesheets.top} />
      <MenuButton {...menuItems.candidate_expenses.top} />
    </Flex>
  )
}

function DesktopDynamicMenuList({ menuItems, id }: { menuItems: MenuItems; id: string }) {
  return (
    <DynamicSingleButtonOrMenuList
      menuItems={menuItems}
      renderSingle={item => <MenuButton {...item} />}
      renderMultiple={(topItem, subItems) => (
        <HoverMenu variant="mainMenu" id={id}>
          <HoverMenuButton {...topItem} />
          <MenuList>
            {Object.entries(subItems).map(([key, item]) => {
              return <DesktopMenuItemLink key={key} {...item} />
            })}
          </MenuList>
        </HoverMenu>
      )}
    />
  )
}

type MenuButtonProps = Omit<ButtonProps, 'leftIcon'> & {
  href?: string
  isHidden?: boolean
  leftIcon?: ReactElement
}

const MenuButton = forwardRef<MenuButtonProps, 'button'>(
  ({ isHidden, leftIcon, rightIcon, children, ...props }, ref) => {
    if (isHidden) return null

    return (
      <Button variant="mainMenu" ref={ref} {...props}>
        {/* German hide icons at 1720px, for English we'd hide icons at 1550px, but we have to hide at widest language */}
        <Box sx={{ '@media(max-width: 1710px)': { display: 'none' } }}>
          {leftIcon &&
            cloneElement(leftIcon as ReactElement<{ className?: string }>, { className: 'menu-button__icon' })}
        </Box>
        {children}
        {rightIcon}
      </Button>
    )
  }
)

MenuButton.displayName = 'MenuButton'

function HoverMenuButton(props: MenuButtonProps) {
  const menuButtonProps = useMenuButton(props)
  if (props.isHidden) return null
  return <MenuButton rightIcon={<ChevronDown className="chevron-icon" />} {...props} {...menuButtonProps} />
}

const DesktopMenuItemLink = forwardRef<MenuItemLinkProps & { leftIcon?: MenuItemLinkProps['icon'] }, 'a'>(
  ({ leftIcon, ...props }, ref) => {
    return <MenuItemLink ref={ref} icon={leftIcon} {...props} />
  }
)

DesktopMenuItemLink.displayName = 'DesktopMenuItemLink'
